/**
 * +acolyte-specs: the Acolyte's three specialisations (docs/design/classes/specs.md "Acolyte"). Built
 * on the Warrior / Mage references: every base key 1–7 evolves on the same key, the signature
 * mechanic lives in passives / auras, spec-only abilities sit on Q (and E).
 *
 *  - Lightbinder (healer, wards and Absolution): Radiant Ward, Haloburst and Quickmend mark allies
 *    with Absolution; 45% of your holy damage heals every Absolved ally (S6 transfer, split when more
 *    than three are marked). Ramp the marks before the damage, then heal by fighting.
 *  - Hemomancer (healer, bleeds to heal): heals cost health, not mana; missing health makes them
 *    stronger (Sanguine Pool); your damage leeches you back up. Martyr takes a share of the party's
 *    damage onto you, then pays it back as healing (S10 damage share).
 *  - Heretic (ranged dps, curses and a horror): Rotcurse ticks pay Tithes (0–5); Soulflay spends one
 *    for a big hit, Call the Hollow One spends three for a horror that fights beside you and spreads
 *    your curse (S7 pet); Unholy Tithe trades health for mana and a Tithe.
 *
 * Names: original (checked against WoW's spell list; names.ts holds the denylist incl. the ones avoided
 * here: the Hemomancer's blessing is Vein Pact, since "Blood Pact" is a WoW Warlock aura).
 * Numbers: tuned with `pnpm harness -- specs` (docs/design/classes/specs.md §7).
 */
import type { AbilityDef, AreaSpec, AuraDef, Condition, EffectSpec, NpcDef, SpecDef } from '@keystone/sim';
import { ABILITY, BASE_ABILITY } from '../../ids';
import { OFF_GCD, SPELL_RANGE, aroundTarget } from '../common';
import { type SpecInfo, infoOf } from './types';

const AC = BASE_ABILITY.acolyte;
const L = ABILITY.lightbinder;
const S = ABILITY.stormcaller;
const W = ABILITY.warden;

export const ACOLYTE_SPEC = { lightbinder: 'lightbinder', hemomancer: 'hemomancer', heretic: 'heretic' } as const;

/** Ability ids (key in the comment). Checked against the legacy `lightbinder.*` kit: no collisions. */
export const LIGHTBINDER = {
  dawnlash: 'lightbinder.dawnlash', // 1 (Lightlash)
  triuneRay: 'lightbinder.triune_ray', // 2 (Dawnbrand)
  quickmend: 'lightbinder.quickmend', // 3 (Tend)
  radiantWard: 'lightbinder.radiant_ward', // 4 (Aegis)
  haloburst: 'lightbinder.haloburst', // 5 (Sanctum Circle)
  lightboundOath: 'lightbinder.lightbound_oath', // 6 (Hearthward)
  blindingVerdict: 'lightbinder.blinding_verdict', // 7 (Dazzle)
  exaltation: 'lightbinder.exaltation', // Q
} as const;
export const LIGHTBINDER_AURA = {
  bond: 'lightbinder.absolution_bond', // passive: the S6 transfer and the Sunpierce proc
  absolution: 'lightbinder.absolution', // the mark (also the id Absolution heals are logged under)
  radiantWard: 'lightbinder.radiant_ward_absorb',
  oath: 'lightbinder.lightbound_oath_buff',
  verdict: 'lightbinder.verdict_stun',
  sentenced: 'lightbinder.sentenced', // +specs balance: Blinding Verdict's mark: your Dawnlashes +50% for 6 s
  exaltation: 'lightbinder.exaltation_buff',
  sunpierce: 'lightbinder.sunpierce', // proc: the next Dawnlash is instant
} as const;

export const HEMOMANCER = {
  bloodBolt: 'hemomancer.blood_bolt', // 1 (Lightlash)
  crimsonBloom: 'hemomancer.crimson_bloom', // 2 (Dawnbrand)
  transfusion: 'hemomancer.transfusion', // 3 (Tend)
  scab: 'hemomancer.scab', // 4 (Aegis)
  bleedfield: 'hemomancer.bleedfield', // 5 (Sanctum Circle)
  veinPact: 'hemomancer.vein_pact', // 6 (Hearthward)
  coagulate: 'hemomancer.coagulate', // 7 (Dazzle)
  martyr: 'hemomancer.martyr', // Q
} as const;
export const HEMOMANCER_AURA = {
  sanguinePool: 'hemomancer.sanguine_pool', // passive: missing health → healing, leech
  scab: 'hemomancer.scab_absorb',
  veinPact: 'hemomancer.vein_pact_buff',
  coagulated: 'hemomancer.coagulated',
  martyr: 'hemomancer.martyr_share', // S10 damage share (heals logged under this id)
  quickened: 'hemomancer.quickened_blood', // proc: the next Transfusion is instant and costs no health
} as const;

export const HERETIC = {
  hexbolt: 'heretic.hexbolt', // 1 (Lightlash)
  rotcurse: 'heretic.rotcurse', // 2 (Dawnbrand)
  unholyTithe: 'heretic.unholy_tithe', // 3 (Tend)
  wraithWard: 'heretic.wraith_ward', // 4 (Aegis)
  blightField: 'heretic.blight_field', // 5 (Sanctum Circle)
  hollowCovenant: 'heretic.hollow_covenant', // 6 (Hearthward)
  dreadGaze: 'heretic.dread_gaze', // 7 (Dazzle)
  soulflay: 'heretic.soulflay', // Q
  callHollowOne: 'heretic.call_hollow_one', // E
  hollowRend: 'heretic.hollow_rend', // the Hollow One's claw (pet attack, not bound)
} as const;
export const HERETIC_AURA = {
  tithing: 'heretic.tithing', // passive: curse ticks pay Tithes, Hollow Whisper procs
  rotcurse: 'heretic.rotcurse_curse',
  wraithWard: 'heretic.wraith_ward_absorb',
  covenant: 'heretic.hollow_covenant_buff',
  dread: 'heretic.dread_gaze_disorient',
  hollowWhisper: 'heretic.hollow_whisper', // proc: the next Soulflay costs no Tithe
} as const;

/** The Hollow One (the Heretic's pet, S7). */
export const HOLLOW_ONE_NPC = 'heretic_hollow_one';

const alliesAround = (radius: number, maxTargets?: number): AreaSpec => ({ anchor: 'caster', shape: { type: 'circle', radius }, affects: 'allies', ...(maxTargets ? { maxTargets } : {}) });
const enemyBolt = { targeting: 'enemy' as const, range: SPELL_RANGE, requiresFacing: true };
const absorbAura = (id: string, name: string, extra: Partial<AuraDef> = {}): AuraDef => ({ id, name, kind: 'buff', durationMs: 12_000, stacking: 'refresh', absorb: {}, ...extra });
/** A party blessing (the evolved Hearthward): 60 minutes, lasts through death. */
const blessing = (id: string, name: string, mods: AuraDef['mods']): EffectSpec => ({
  type: 'applyAura',
  to: 'area',
  area: alliesAround(40),
  aura: { id, name, kind: 'buff', durationMs: 3_600_000, stacking: 'unique', mods, flags: { persistThroughDeath: true } },
});

// ============================================================================
// Lightbinder (healer): wards, Absolution, healing by fighting
// ============================================================================

const LB = LIGHTBINDER;
const LBA = LIGHTBINDER_AURA;

/** The mark: while it lasts, 45% of the Lightbinder's holy damage heals this ally (S6, the bond passive). */
const absolution: AuraDef = { id: LBA.absolution, name: 'Absolution', kind: 'buff', durationMs: 15_000, stacking: 'refresh' };
const markTarget: EffectSpec = { type: 'applyAura', aura: absolution };

const sunpierce: AuraDef = {
  id: LBA.sunpierce,
  name: 'Sunpierce',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [LB.dawnlash] }],
  procs: [{ on: 'castResolved', abilityIds: [LB.dawnlash], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [LB.dawnlash] } },
};

/** The signature passive: Absolution (the S6 transfer) and the Sunpierce proc. */
const absolutionBond: AuraDef = {
  id: LBA.bond,
  name: 'Absolution',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  transfer: { pct: 0.45, markAuraId: LBA.absolution, schools: ['holy'], radius: SPELL_RANGE, shareAbove: 3 },
  // Every Triune Ray bolt may set the next Dawnlash alight (instant).
  procs: [{ on: 'damageDealt', abilityIds: [LB.triuneRay], chance: 0.3, badLuck: { step: 0.08 }, effects: [{ type: 'applyAura', to: 'self', aura: sunpierce }] }],
};

const radiantWardAura: AuraDef = absorbAura(LBA.radiantWard, 'Radiant Ward', { durationMs: 15_000 });

const exaltationBuff: AuraDef = {
  id: LBA.exaltation,
  name: 'Exaltation',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [
    { stat: 'resourceCostPct', value: -1, abilityIds: [LB.radiantWard] },
    { stat: 'castTimePct', value: -1, abilityIds: [LB.quickmend] },
    { stat: 'damageDonePct', value: 0.15, school: 'holy' },
  ],
  // Every ward goes out again at once: no cooldown while it lasts.
  procs: [{ on: 'castResolved', abilityIds: [LB.radiantWard], effects: [{ type: 'modifyCooldown', abilityId: LB.radiantWard, reset: true }] }],
  ui: { proc: true, glow: { abilityIds: [LB.radiantWard] } },
};

const verdictStun: AuraDef = { id: LBA.verdict, name: 'Blinding Verdict', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'stun' };
/** +specs balance: the Verdict's sentence. Your Dawnlashes strike the sentenced enemy 50% harder for 6 s (the solo opener). */
const sentenced: AuraDef = { id: LBA.sentenced, name: 'Sentenced', kind: 'debuff', durationMs: 6000, stacking: 'refresh', ui: { glow: { abilityIds: [LB.dawnlash] } } };

const dawnlash: AbilityDef = {
  id: LB.dawnlash,
  name: 'Dawnlash',
  school: 'holy',
  description: 'Lash an enemy with dawnlight. Through Absolution, 45% of the damage heals every ally you have marked. 50% harder into an enemy Sentenced by your Blinding Verdict.',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 110 }],
  // +specs balance (Sentenced): the solo decision. Open with Blinding Verdict (a 3 s stun, 6 s of
  // Dawnlashes 50% harder, no hits taken while it reels) or hold it as your interrupt.
  effects: [{ type: 'damage', school: 'holy', amount: { coef: 5.2, variance: 0.05, perStack: { auraId: LBA.sentenced, on: 'target', mult: 0.5 } } }],
  ui: {
    tags: ['builder'],
    color: '#ffd166',
    animAs: L.judgment,
    desc: 'Lash an enemy for {damage} Holy damage, 50% more while it is Sentenced by your Blinding Verdict. Absolution turns 45% of it into healing on every ally you have marked (split when more than three are marked).',
    hint: 'Press 1 to fight: every hit heals the allies you warded.',
  },
};

const triuneRay: AbilityDef = {
  id: LB.triuneRay,
  name: 'Triune Ray',
  school: 'holy',
  description: 'Channel three bolts of light into an enemy over 2 s. Each may light Sunpierce (the next Dawnlash is instant).',
  ...enemyBolt,
  cast: { type: 'channel', durationMs: 2000, ticks: 3 },
  cooldown: { durationMs: 9000 },
  cost: [{ resource: 'mana', amount: 200 }],
  effects: [{ type: 'damage', school: 'holy', amount: { coef: 2.1, variance: 0.05 } }],
  ui: {
    tags: ['cooldown'],
    color: '#fff1a8',
    animAs: L.holyShock,
    desc: 'Channel three bolts of light, each dealing {damage} Holy damage (Absolution heals your marked allies for 45% of it). Each bolt has a chance to light Sunpierce: your next Dawnlash is instant.',
    hint: 'Press 2 on cooldown: your strongest heal is this damage.',
  },
};

const quickmend: AbilityDef = {
  id: LB.quickmend,
  name: 'Quickmend',
  school: 'holy',
  description: 'A fast emergency heal that also marks the ally with Absolution.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cast: { type: 'cast', timeMs: 1000 },
  cost: [{ resource: 'mana', amount: 340 }],
  effects: [{ type: 'heal', amount: { coef: 5.0, variance: 0.05 } }, markTarget],
  ui: { tags: ['heal'], color: '#fff3c4', animAs: L.flashHeal, desc: 'Heal an ally for {heal} and mark them with Absolution for 15 sec. 1 sec cast, expensive.', hint: 'Press 3 when someone is about to die.' },
};

const radiantWard: AbilityDef = {
  id: LB.radiantWard,
  name: 'Radiant Ward',
  school: 'holy',
  description: "Ward an ally with radiant light, absorbing damage equal to 20% of the Lightbinder's maximum health for 15 s, and mark them with Absolution.",
  targeting: 'ally',
  range: SPELL_RANGE,
  cooldown: { durationMs: 6000 },
  cost: [{ resource: 'mana', amount: 360 }],
  effects: [{ type: 'absorb', auraId: LBA.radiantWard, amount: { pctSourceMaxHp: 0.2 } }, markTarget],
  ui: {
    tags: ['externalDefensive'],
    color: '#ffd166',
    animAs: L.guardiansAegis,
    desc: 'Ward an ally (or yourself) to absorb {absorb} damage for 15 sec and mark them with Absolution: 45% of your holy damage heals them.',
    hint: 'Press 4 on the tank, then on everyone else: ramp the wards before the damage lands.',
  },
};

const haloburst: AbilityDef = {
  id: LB.haloburst,
  name: 'Haloburst',
  school: 'holy',
  description: 'A ring of light bursts from you: up to 5 allies within 40 yd (the most injured first) are healed a little, and up to 5 other allies are marked with Absolution.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  cost: [{ resource: 'mana', amount: 550 }],
  effects: [
    { type: 'heal', to: 'area', area: alliesAround(SPELL_RANGE), smart: 5, amount: { coef: 1.5, variance: 0.05 } },
    // Marks the others (you mark yourself with a ward when you fight alone).
    { type: 'applyAura', to: 'area', area: { ...alliesAround(SPELL_RANGE, 5), affects: 'allies_not_self' }, aura: absolution },
  ],
  ui: { tags: ['groupHeal'], color: '#ffe9a6', animAs: L.radiantWave, desc: 'Heal up to 5 allies within 40 yd for {heal} (the most injured first) and mark up to 5 allies around you (not yourself) with Absolution for 15 sec. Instant.', hint: 'Press 5 before a big pull: the whole party is marked at once.' },
};

const lightboundOath: AbilityDef = {
  id: LB.lightboundOath,
  name: 'Lightbound Oath',
  school: 'holy',
  description: 'Bless every ally within 40 yd with +10% maximum health and +5% healing taken for 60 minutes. It lasts through death.',
  targeting: 'none',
  cost: [{ resource: 'mana', amount: 400 }],
  effects: [blessing(LBA.oath, 'Lightbound Oath', [{ stat: 'maxHpPct', value: 0.1 }, { stat: 'healingTakenPct', value: 0.05 }])],
  ui: { tags: ['cooldown'], color: '#f4b860', animAs: AC.hearthward, desc: 'Bless every ally within 40 yd (you included) with Lightbound Oath: +10% maximum health and +5% healing taken for 60 min. It lasts through death.', hint: 'Press 6 once an hour: health and healing for the party.' },
};

const blindingVerdict: AbilityDef = {
  id: LB.blindingVerdict,
  name: 'Blinding Verdict',
  school: 'holy',
  description: 'A blinding verdict stuns an enemy for 3 s; the flash sears it and heals every Absolved ally. Sentenced: your Dawnlashes strike it 50% harder for 6 s.',
  targeting: 'enemy',
  range: 30,
  cooldown: { durationMs: 30_000 },
  cost: [{ resource: 'mana', amount: 150 }],
  effects: [
    { type: 'damage', school: 'holy', amount: { coef: 1.0, variance: 0.05 } },
    { type: 'applyAura', aura: verdictStun },
    { type: 'applyAura', aura: sentenced },
    { type: 'heal', to: 'area', area: alliesAround(SPELL_RANGE), if: { type: 'targetHasAura', auraId: LBA.absolution, fromSelf: true }, amount: { coef: 1.0, variance: 0.05 } },
  ],
  ui: { tags: ['cooldown', 'interrupt'], color: '#fff3b0', animAs: AC.dazzle, desc: 'Stun an enemy for 3 sec and sear it for {damage} Holy damage; the flash heals every ally marked with your Absolution for {heal}. Sentenced: for 6 sec your Dawnlashes strike it 50% harder.', hint: 'Press 7 to stop a cast, as a burst heal for your marked allies, or alone to open a fight with Sentenced Dawnlashes.' },
};

const exaltation: AbilityDef = {
  id: LB.exaltation,
  name: 'Exaltation',
  school: 'holy',
  description: 'For 10 s Radiant Ward costs nothing and has no cooldown, Quickmend is instant, and your holy damage is 15% stronger.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90_000 },
  effects: [{ type: 'applyAura', to: 'self', aura: exaltationBuff }],
  ui: { tags: ['cooldown'], color: '#fff7d6', animAs: L.dawnbringer, desc: 'For 10 sec: Radiant Ward is free and comes back at once, Quickmend is instant, and you deal 15% more Holy damage (so Absolution heals 15% more). Off the global cooldown.', hint: 'Press Q before the big hit: ward the whole party, then lash.' },
};

export const lightbinderSpec: SpecDef = {
  id: ACOLYTE_SPEC.lightbinder,
  baseClassId: 'acolyte',
  name: 'Lightbinder',
  role: 'healer',
  unlockLevel: 10,
  evolutions: {
    [AC.lightlash]: LB.dawnlash,
    [AC.dawnbrand]: LB.triuneRay,
    [AC.tend]: LB.quickmend,
    [AC.aegis]: LB.radiantWard,
    [AC.sanctumCircle]: LB.haloburst,
    [AC.hearthward]: LB.lightboundOath,
    [AC.dazzle]: LB.blindingVerdict,
  },
  extraAbilities: [{ key: 'Q', abilityId: LB.exaltation }],
  abilities: [dawnlash, triuneRay, quickmend, radiantWard, haloburst, lightboundOath, blindingVerdict, exaltation],
  resources: [{ id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 95, regenPerSecOoc: 600 }],
  passives: [absolutionBond],
  auras: [absolution, radiantWardAura, exaltationBuff, verdictStun, sentenced, sunpierce],
  color: '#ffd166',
  ui: {
    tagline: 'Shields first, then heals by dealing holy damage through Absolution.',
    fantasy: 'A ward healer. You prevent damage before it lands with radiant wards, and your Dawnlash and Triune Ray flow back into everyone you have marked with Absolution. You heal by fighting.',
    signature: 'Absolution: Radiant Ward, Haloburst and Quickmend mark an ally for 15 s; while marked, 45% of your holy damage heals them (split when more than three are marked).',
    weapon: 'censer',
    tint: '#ffd166',
    glow: '#fff3c4',
  },
};

// ============================================================================
// Hemomancer (healer): heals cost health; missing health heals harder; damage refills you
// ============================================================================

const HM = HEMOMANCER;
const HMA = HEMOMANCER_AURA;

/** Free inside Quickened Blood (Transfusion only). */
const notQuickened: Condition = { type: 'selfLacksAura', auraId: HMA.quickened };
/** Spend `pct` of your maximum health (self-harm: never lethal). */
const bleed = (pct: number, freeWhen?: Condition): EffectSpec => ({ type: 'damage', to: 'self', school: 'shadow', amount: { pctSourceMaxHp: pct }, selfHarm: true, ...(freeWhen ? { if: freeWhen } : {}) });
/** A health-cost spell needs this much health left (you can't heal from your last drop). */
const affords = (pct: number): Condition => ({ type: 'not', cond: { type: 'selfHpBelow', pct } });

const quickened: AuraDef = {
  id: HMA.quickened,
  name: 'Quickened Blood',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [HM.transfusion] }],
  procs: [{ on: 'castResolved', abilityIds: [HM.transfusion], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [HM.transfusion] } },
};

/** The signature passive: missing health → healing; your damage leeches you back up. */
const sanguinePool: AuraDef = {
  id: HMA.sanguinePool,
  name: 'Sanguine Pool',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { persistThroughDeath: true },
  mods: [
    { stat: 'healingDonePct', value: 0.8, perMissingHp: true },
    { stat: 'leechPct', value: 0.6 },
  ],
  // Blood Bolt may quicken your blood: the next Transfusion is instant and costs no health.
  procs: [{ on: 'damageDealt', abilityIds: [HM.bloodBolt], chance: 0.22, badLuck: { step: 0.06 }, effects: [{ type: 'applyAura', to: 'self', aura: quickened }] }],
};

const scabAura: AuraDef = absorbAura(HMA.scab, 'Scab');
const coagulated: AuraDef = { id: HMA.coagulated, name: 'Coagulate', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'stun' };
const martyrShare: AuraDef = {
  id: HMA.martyr,
  name: 'Martyr',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  damageShare: { pct: 0.3, radius: 30, healBackPct: 1 },
  mods: [{ stat: 'damageTakenPct', value: -0.15 }],
  ui: { proc: true },
};

const bloodBolt: AbilityDef = {
  id: HM.bloodBolt,
  name: 'Blood Bolt',
  school: 'shadow',
  description: 'A bolt of drawn blood. Sanguine Pool leeches 60% of all your damage back to you; Blood Bolt may quicken your next Transfusion.',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 60 }],
  effects: [{ type: 'damage', school: 'shadow', amount: { coef: 4.2, variance: 0.05 } }],
  ui: {
    tags: ['builder'],
    color: '#c2183a',
    animAs: L.judgment,
    desc: 'Hurl a bolt of blood for {damage} Shadow damage; 60% of it flows back to you as health. May Quicken your blood: your next Transfusion is instant and costs no health.',
    hint: 'Press 1 to drink: your damage is how you refill your health.',
  },
};

const crimsonBloom: AbilityDef = {
  id: HM.crimsonBloom,
  name: 'Crimson Bloom',
  school: 'shadow',
  description: 'Costs 5% of your health: a pool of blood blooms under an ally for 8 s, healing every ally standing in it each second.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cooldown: { durationMs: 12_000 },
  usableIf: affords(0.15),
  effects: [
    { type: 'zone', zoneId: HM.crimsonBloom, anchor: 'target', shape: { type: 'circle', radius: 7 }, durationMs: 8000, affects: 'allies', tick: { intervalMs: 1000, effects: [{ type: 'heal', amount: { coef: 0.85 } }] }, snapshot: true },
    bleed(0.05),
  ],
  ui: { tags: ['groupHeal'], color: '#e03a5a', animAs: W.consecratedGround, desc: 'Costs 5% of your health. A pool of blood blooms under an ally for 8 sec: every ally inside is healed for {heal} each second (stronger the lower you are when you cast it).', hint: 'Press 2 under the tank when the group stacks up.' },
};

const transfusion: AbilityDef = {
  id: HM.transfusion,
  name: 'Transfusion',
  school: 'shadow',
  description: 'Costs 7% of your health: pour your own blood into an ally. Heals more the lower your health is.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cast: { type: 'cast', timeMs: 1200 },
  usableIf: { type: 'or', any: [affords(0.2), { type: 'selfHasAura', auraId: HMA.quickened }] },
  effects: [{ type: 'heal', amount: { coef: 5.6, variance: 0.05 } }, bleed(0.07, notQuickened)],
  ui: { tags: ['heal'], color: '#ff4a64', animAs: L.flashHeal, desc: 'Costs 7% of your health. Heal an ally for {heal}, up to 80% more the lower your own health is. Needs at least 20% health (Quickened Blood makes it instant and free).', hint: 'Press 3 to heal: you pay in blood, and bleeding harder heals harder.' },
};

const scab: AbilityDef = {
  id: HM.scab,
  name: 'Scab',
  school: 'shadow',
  description: "Costs 5% of your health: a crust of blood shields an ally, absorbing damage equal to 22% of the Hemomancer's maximum health for 12 s.",
  targeting: 'ally',
  range: SPELL_RANGE,
  cooldown: { durationMs: 15_000 },
  usableIf: affords(0.15),
  effects: [{ type: 'absorb', auraId: HMA.scab, amount: { pctSourceMaxHp: 0.22 } }, bleed(0.05)],
  ui: { tags: ['externalDefensive'], color: '#9e1030', animAs: L.guardiansAegis, desc: 'Costs 5% of your health. Shield an ally (or yourself) with a scab of blood that absorbs {absorb} damage for 12 sec.', hint: 'Press 4 on the tank before a big hit.' },
};

const bleedfield: AbilityDef = {
  id: HM.bleedfield,
  name: 'Bleedfield',
  school: 'shadow',
  description: 'Open the ground under your enemies for 8 s: every enemy inside bleeds each second, and every bleed heals your most injured ally.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 18_000 },
  cost: [{ resource: 'mana', amount: 300 }],
  effects: [
    {
      type: 'zone',
      zoneId: HM.bleedfield,
      anchor: 'point',
      shape: { type: 'circle', radius: 8 },
      durationMs: 8000,
      affects: 'enemies',
      tick: {
        intervalMs: 1000,
        effects: [
          { type: 'damage', school: 'shadow', amount: { coef: 0.55 }, isAoE: true },
          { type: 'heal', to: 'area', area: alliesAround(SPELL_RANGE), smart: 1, amount: { coef: 0.45 } },
        ],
      },
    },
  ],
  ui: { tags: ['aoe', 'groupHeal'], color: '#b3122e', animAs: AC.sanctumCircle, desc: 'For 8 sec every enemy in the field bleeds for {damage} Shadow damage each second, and every bleed heals your most injured ally for {heal}. Costs mana, not health. Instant.', hint: 'Press 5 on a pack: the more enemies bleed, the more you heal.' },
};

const veinPact: AbilityDef = {
  id: HM.veinPact,
  name: 'Vein Pact',
  school: 'shadow',
  description: 'Bless every ally within 40 yd with +10% maximum health and 3% leech for 60 minutes. It lasts through death.',
  targeting: 'none',
  cost: [{ resource: 'mana', amount: 400 }],
  effects: [blessing(HMA.veinPact, 'Vein Pact', [{ stat: 'maxHpPct', value: 0.1 }, { stat: 'leechPct', value: 0.03 }])],
  ui: { tags: ['cooldown'], color: '#c2183a', animAs: AC.hearthward, desc: 'Bless every ally within 40 yd (you included) with Vein Pact: +10% maximum health, and 3% of the damage they deal heals them, for 60 min. It lasts through death.', hint: 'Press 6 once an hour: health and leech for the party.' },
};

const coagulate: AbilityDef = {
  id: HM.coagulate,
  name: 'Coagulate',
  school: 'shadow',
  description: "Clot an enemy's blood: stunned for 3 s and wounded (the wound leeches back to you).",
  targeting: 'enemy',
  range: 30,
  cooldown: { durationMs: 30_000 },
  cost: [{ resource: 'mana', amount: 150 }],
  effects: [{ type: 'damage', school: 'shadow', amount: { coef: 0.8, variance: 0.05 } }, { type: 'applyAura', aura: coagulated }],
  ui: { tags: ['cooldown', 'interrupt'], color: '#8a0f26', animAs: AC.dazzle, desc: 'Stun an enemy for 3 sec and wound it for {damage} Shadow damage.', hint: 'Press 7 to stop a cast or a charging enemy.' },
};

const martyr: AbilityDef = {
  id: HM.martyr,
  name: 'Martyr',
  school: 'shadow',
  description: "For 8 s, 30% of the damage your allies within 30 yd take is dealt to you instead (it can't kill you; you take 15% less). When it ends you heal them for all of it.",
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90_000 },
  effects: [{ type: 'applyAura', to: 'self', aura: martyrShare }],
  ui: { tags: ['cooldown', 'groupHeal'], color: '#ff2f4f', animAs: L.divineShield, desc: "For 8 sec, 30% of the damage your allies within 30 yd take is dealt to you instead (it can't kill you, and you take 15% less). When it ends, you heal them for everything you took. Off the global cooldown.", hint: 'Press Q when the party is about to get hit hard: take the blood onto yourself.' },
};

export const hemomancerSpec: SpecDef = {
  id: ACOLYTE_SPEC.hemomancer,
  baseClassId: 'acolyte',
  name: 'Hemomancer',
  role: 'healer',
  unlockLevel: 10,
  evolutions: {
    [AC.lightlash]: HM.bloodBolt,
    [AC.dawnbrand]: HM.crimsonBloom,
    [AC.tend]: HM.transfusion,
    [AC.aegis]: HM.scab,
    [AC.sanctumCircle]: HM.bleedfield,
    [AC.hearthward]: HM.veinPact,
    [AC.dazzle]: HM.coagulate,
  },
  extraAbilities: [{ key: 'Q', abilityId: HM.martyr }],
  abilities: [bloodBolt, crimsonBloom, transfusion, scab, bleedfield, veinPact, coagulate, martyr],
  // Mana only pays for Blood Bolt, Bleedfield and the utility: health is the real budget.
  resources: [{ id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 90, regenPerSecOoc: 600 }],
  passives: [sanguinePool],
  auras: [scabAura, coagulated, martyrShare, quickened],
  color: '#b3122e',
  ui: {
    tagline: 'A healer who bleeds to heal; the lower you go, the more you save.',
    fantasy: 'Blood magic for good. You open your own veins to mend allies, and your missing health turns into raw healing power. Nothing feels more heroic than pulling a tank back from 1%.',
    signature: 'Sanguine Pool: heals cost health, not mana; missing health boosts healing (up to +80%); 60% of the damage you deal refills you.',
    weapon: 'blood_censer',
    tint: '#b3122e',
    glow: '#ff6a7a',
  },
};

// ============================================================================
// Heretic (ranged dps): curses tick Tithes; Soulflay, the Hollow One
// ============================================================================

const HR = HERETIC;
const HRA = HERETIC_AURA;
const TITHES = 'tithes';

/** The curse every Heretic tool spreads. Its ticks pay Tithes (the Tithing passive). */
const rotcurseCurse: AuraDef = {
  id: HRA.rotcurse,
  name: 'Rotcurse',
  kind: 'debuff',
  durationMs: 18_000,
  stacking: 'refresh',
  pandemic: true,
  dispelType: 'curse',
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'shadow', amount: { coef: 1.05 } }] },
};
const curse: EffectSpec = { type: 'applyAura', aura: rotcurseCurse };
/** Spread the curse to up to `n` enemies near the target that don't carry it yet. */
const spreadCurse = (radius: number, n: number): EffectSpec => ({
  type: 'applyAura',
  to: 'area',
  area: { ...aroundTarget(radius, n), excludePrimary: true },
  if: { type: 'targetLacksAura', auraId: HRA.rotcurse, fromSelf: true },
  aura: rotcurseCurse,
});

const hollowWhisper: AuraDef = {
  id: HRA.hollowWhisper,
  name: 'Hollow Whisper',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'refresh',
  mods: [{ stat: 'resourceCostPct', value: -1, abilityIds: [HR.soulflay] }],
  procs: [{ on: 'castResolved', abilityIds: [HR.soulflay], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [HR.soulflay] } },
};

/**
 * The signature passive: curse ticks pay Tithes; Hexbolts into a curse may whisper (a free Soulflay);
 * Grave Tithe: every enemy that dies with you in the fight pays a Tithe (the solo engine: a field mob
 * dies before its curse pays, so each kill banks the next fight's opening Soulflay).
 */
const tithing: AuraDef = {
  id: HRA.tithing,
  name: 'Tithes',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [
    { on: 'damageDealt', abilityIds: [HRA.rotcurse], chance: 0.2, badLuck: { step: 0.06 }, effects: [{ type: 'resource', to: 'self', resource: TITHES, amount: 1 }] },
    { on: 'damageDealt', abilityIds: [HR.hexbolt], chance: 0.3, badLuck: { step: 0.05 }, if: { type: 'targetHasAura', auraId: HRA.rotcurse, fromSelf: true }, effects: [{ type: 'applyAura', to: 'self', aura: hollowWhisper }] },
    // Grave Tithe: a kill pays.
    { on: 'killCredit', effects: [{ type: 'resource', to: 'self', resource: TITHES, amount: 1 }] },
  ],
};

const wraithWardAura: AuraDef = absorbAura(HRA.wraithWard, 'Wraith Ward', { durationMs: 10_000 });
const covenantBuff = HRA.covenant;
const dread: AuraDef = { id: HRA.dread, name: 'Dread Gaze', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'disorient' };

const hexbolt: AbilityDef = {
  id: HR.hexbolt,
  name: 'Hexbolt',
  school: 'shadow',
  description: 'A bolt of hex-fire. Into a cursed enemy it may stir a Hollow Whisper (the next Soulflay costs no Tithe).',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 110 }],
  effects: [{ type: 'damage', school: 'shadow', amount: { coef: 4.25, variance: 0.05 } }],
  ui: { tags: ['builder'], color: '#9a5ae0', animAs: S.lightningBolt, desc: 'Hurl a bolt of hex-fire for {damage} Shadow damage. Into an enemy carrying your Rotcurse it may stir a Hollow Whisper: your next Soulflay costs no Tithe.', hint: 'Press 1 between curses: your filler.' },
};

const rotcurse: AbilityDef = {
  id: HR.rotcurse,
  name: 'Rotcurse',
  school: 'shadow',
  description: 'Curse an enemy to rot for 18 s; the curse leaps to up to 2 nearby enemies that lack it. Every tick may pay you a Tithe.',
  ...enemyBolt,
  cost: [{ resource: 'mana', amount: 140 }],
  effects: [curse, spreadCurse(10, 2)],
  ui: { tags: ['cooldown', 'aoe'], color: '#7a3fb8', animAs: S.flameShock, desc: 'Curse an enemy: it rots for {damage} Shadow damage every 2 sec for 18 sec, and the curse leaps to up to 2 enemies within 10 yd that lack it. Each tick has a 20% chance to pay you a Tithe. Instant.', hint: 'Press 2 on every enemy: curses are where your Tithes come from.' },
};

const unholyTithe: AbilityDef = {
  id: HR.unholyTithe,
  name: 'Unholy Tithe',
  school: 'shadow',
  description: 'Pay 8% of your health to the hollow: gain 15% of your mana and a Tithe.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12_000 },
  usableIf: { type: 'not', cond: { type: 'selfHpBelow', pct: 0.3 } },
  gain: [{ resource: TITHES, amount: 1 }, { resource: 'mana', amount: 1500 }],
  effects: [{ type: 'damage', to: 'self', school: 'shadow', amount: { pctSourceMaxHp: 0.08 }, selfHarm: true }],
  ui: { tags: ['cooldown'], color: '#5e2a8a', animAs: S.astralShift, desc: 'Pay 8% of your health: gain 15% of your maximum mana and a Tithe. Needs 30% health. Off the global cooldown.', hint: 'Press 3 when you are a Tithe short of the Hollow One, or low on mana.' },
};

const wraithWard: AbilityDef = {
  id: HR.wraithWard,
  name: 'Wraith Ward',
  school: 'shadow',
  description: 'Wrap yourself in wraiths: absorbs 22% of your maximum health for 10 s, 32% while you hold 3 or more Tithes.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 30_000 },
  effects: [
    {
      type: 'conditional',
      cond: { type: 'resourceAtLeast', resource: TITHES, amount: 3 },
      then: [{ type: 'absorb', to: 'self', auraId: HRA.wraithWard, amount: { pctSourceMaxHp: 0.32 } }],
      else: [{ type: 'absorb', to: 'self', auraId: HRA.wraithWard, amount: { pctSourceMaxHp: 0.22 } }],
    },
  ],
  ui: { tags: ['defensive'], color: '#6d4a9e', animAs: AC.aegis, desc: 'Absorb {absorb} damage for 10 sec (22% of your maximum health; 32% while you hold 3 or more Tithes). Off the global cooldown.', hint: 'Press 4 when something hits you.' },
};

const blightField: AbilityDef = {
  id: HR.blightField,
  name: 'Blight Field',
  school: 'shadow',
  description: 'Blight the ground for 8 s: every enemy inside withers each second and is cursed with Rotcurse.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 18_000 },
  cost: [{ resource: 'mana', amount: 350 }],
  effects: [
    {
      type: 'zone',
      zoneId: HR.blightField,
      anchor: 'point',
      shape: { type: 'circle', radius: 8 },
      durationMs: 8000,
      affects: 'enemies',
      tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'shadow', amount: { coef: 0.55 }, isAoE: true }, curse] },
      snapshot: true,
    },
  ],
  ui: { tags: ['aoe'], color: '#5b2c86', animAs: W.consecratedGround, desc: 'Blight the ground for 8 sec: every enemy inside withers for {damage} Shadow damage each second and carries your Rotcurse. Instant, usable on the move.', hint: 'Press 5 on a pack: one press curses them all.' },
};

const hollowCovenant: AbilityDef = {
  id: HR.hollowCovenant,
  name: 'Hollow Covenant',
  school: 'shadow',
  description: 'Bind every ally within 40 yd to a hollow covenant: +10% maximum health and +3% damage for 60 minutes. It lasts through death.',
  targeting: 'none',
  cost: [{ resource: 'mana', amount: 400 }],
  effects: [blessing(covenantBuff, 'Hollow Covenant', [{ stat: 'maxHpPct', value: 0.1 }, { stat: 'damageDonePct', value: 0.03 }])],
  ui: { tags: ['cooldown'], color: '#8e5cc4', animAs: AC.hearthward, desc: 'Bind every ally within 40 yd (you included) to the Hollow Covenant: +10% maximum health and +3% damage for 60 min. It lasts through death.', hint: 'Press 6 once an hour.' },
};

const dreadGaze: AbilityDef = {
  id: HR.dreadGaze,
  name: 'Dread Gaze',
  school: 'shadow',
  description: 'Fix an enemy with a hollow stare: disoriented for 3 s.',
  targeting: 'enemy',
  range: 30,
  cooldown: { durationMs: 30_000 },
  cost: [{ resource: 'mana', amount: 120 }],
  effects: [{ type: 'applyAura', aura: dread }],
  ui: { tags: ['cooldown', 'interrupt'], color: '#b07ae8', animAs: AC.dazzle, desc: 'Disorient an enemy for 3 sec (it stops casting and wanders).', hint: 'Press 7 to stop a cast or peel a melee enemy.' },
};

const soulflay: AbilityDef = {
  id: HR.soulflay,
  name: 'Soulflay',
  school: 'shadow',
  description: 'Spend a Tithe: flay the soul of an enemy for heavy shadow damage and drink a little of it. Every other enemy within 8 yd carrying your Rotcurse takes a quarter of it.',
  ...enemyBolt,
  cost: [{ resource: TITHES, amount: 1 }],
  effects: [
    { type: 'damage', school: 'shadow', amount: { coef: 6.4, variance: 0.05 } },
    // The flaying echoes through the curse: every other cursed enemy nearby takes a quarter.
    { type: 'damage', school: 'shadow', to: 'area', area: { ...aroundTarget(8, 8), excludePrimary: true }, if: { type: 'targetHasAura', auraId: HRA.rotcurse, fromSelf: true }, amount: { coef: 1.6, variance: 0.05 }, isAoE: true },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.02 } },
  ],
  ui: { tags: ['spender'], color: '#c98cff', animAs: S.lavaBurst, desc: 'Spend a Tithe: flay an enemy for {damage} Shadow damage, and every other enemy within 8 yd carrying your Rotcurse for {damage2}. Heals you for 2% of your maximum health. Instant.', hint: 'Press Q at 2+ Tithes: never sit at 5.' },
};

const hollowRend: AbilityDef = {
  id: HR.hollowRend,
  name: 'Hollow Rend',
  school: 'shadow',
  description: "The Hollow One's claws: they rend the enemy and carry your Rotcurse to the enemies around it.",
  targeting: 'enemy',
  effects: [{ type: 'damage', school: 'shadow', amount: { coef: 2.3, variance: 0.05 } }, curse, spreadCurse(8, 2)],
  ui: { tags: ['aoe'], color: '#c98cff', animAs: W.cleaveStrike, desc: "The Hollow One rends its target for {damage} Shadow damage, curses it, and carries your Rotcurse to up to 2 enemies within 8 yd that lack it.", hint: 'The Hollow One attacks on its own.' },
};

const callHollowOne: AbilityDef = {
  id: HR.callHollowOne,
  name: 'Call the Hollow One',
  school: 'shadow',
  description: 'Spend 3 Tithes: call a horror through for 15 s. It fights your target, rends it every 1.5 s and spreads your Rotcurse to everything it touches.',
  ...enemyBolt,
  cooldown: { durationMs: 60_000 },
  cost: [{ resource: TITHES, amount: 3 }],
  effects: [curse, { type: 'summon', npcId: HOLLOW_ONE_NPC, count: 1, radius: 2.5, anchor: 'target', pet: { durationMs: 15_000, attackAbilityId: HR.hollowRend, intervalMs: 1500 } }],
  ui: { tags: ['cooldown', 'aoe'], color: '#7a3fb8', animAs: S.stormkeeper, desc: 'Spend 3 Tithes: a horror claws through beside your target for 15 sec. Every 1.5 sec it rends its target for {damage} Shadow damage and carries your Rotcurse to the enemies around it.', hint: 'Press E at 3 Tithes on a big pull or a boss.' },
};

/** The Hollow One: a friendly horror (S7 pet). It never takes damage; it attacks as its owner. */
export const hollowOneNpc: NpcDef = {
  id: HOLLOW_ONE_NPC,
  name: 'The Hollow One',
  tier: 'standard',
  hpMult: 1,
  forces: 0,
  abilities: [],
  radius: 0.9,
  combatReach: 1.5,
  moveSpeed: 9,
  ccImmune: true,
  color: '#7a3fb8',
};

export const hereticSpec: SpecDef = {
  id: ACOLYTE_SPEC.heretic,
  baseClassId: 'acolyte',
  name: 'Heretic',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [AC.lightlash]: HR.hexbolt,
    [AC.dawnbrand]: HR.rotcurse,
    [AC.tend]: HR.unholyTithe,
    [AC.aegis]: HR.wraithWard,
    [AC.sanctumCircle]: HR.blightField,
    [AC.hearthward]: HR.hollowCovenant,
    [AC.dazzle]: HR.dreadGaze,
  },
  extraAbilities: [
    { key: 'Q', abilityId: HR.soulflay },
    { key: 'E', abilityId: HR.callHollowOne },
  ],
  abilities: [hexbolt, rotcurse, unholyTithe, wraithWard, blightField, hollowCovenant, dreadGaze, soulflay, callHollowOne, hollowRend],
  // Mana stays the bar; Tithes (0–5) are the pips. They drain slowly out of combat.
  resources: [
    { id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 90, regenPerSecOoc: 600 },
    { id: TITHES, name: 'Tithes', max: 5, start: 0, regenPerSecOoc: -0.05, oocSettle: 0 }, // a Tithe lasts 20 s out of combat (the walk to the next pull)
  ],
  passives: [tithing],
  auras: [rotcurseCurse, hollowWhisper, wraithWardAura, dread],
  color: '#7a3fb8',
  ui: {
    tagline: 'A fallen acolyte who trades in curses, tithes and a summoned horror.',
    fantasy: 'You prayed and nothing answered, so you found something that would. Stack curses that rot a whole pack, harvest the tithes that tick out of them, and spend them to call a horror through.',
    signature: 'Tithes (0–5): Rotcurse ticks pay Tithes; Soulflay spends one, Call the Hollow One spends three for a horror that spreads your curse; Unholy Tithe trades health for mana and a Tithe.',
    weapon: 'tome',
    tint: '#7a3fb8',
    glow: '#c98cff',
  },
};

/** Implemented Acolyte specs (Tower Warden, harness, bots). */
export const ACOLYTE_SPECS: SpecDef[] = [lightbinderSpec, hereticSpec, hemomancerSpec];

/** NPCs the Acolyte specs summon (the content bundle registers them). */
export const ACOLYTE_NPCS: NpcDef[] = [hollowOneNpc];

/** Preview info for all three (the launch set is bold in advancement.md). */
export const ACOLYTE_SPEC_INFO: SpecInfo[] = [infoOf(lightbinderSpec, true), infoOf(hereticSpec, true), infoOf(hemomancerSpec, false)];
